import React from 'react';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  matchPath,
} from 'react-router-dom';
import { ScreenFrame, HTabs, PageTitle, HigloLogo } from '../h';
import { HearthsTab } from './HearthsTab';
import { HearthDetail } from './HearthDetail';
import { DeviceSettingsView } from './DeviceSettingsView';
import { HiglosTab } from '../HiglosTab/HiglosTab';
import { HigloDetail } from '../HiglosTab/HigloDetail';
import { PositionPicker } from '../HiglosTab/PositionPicker';
import { InviteFamilyView } from '../HiglosTab/InviteFamilyView';
import { HelpTab } from '../HelpTab/HelpTab';
import { ArticleView } from '../HelpTab/ArticleView';
import { MessageUsView } from '../HelpTab/MessageUsView';
import { useSupportUnread } from '../HelpTab/useSupportUnread';
import { AccountTab } from '../AccountTab/AccountTab';
import { useAccountInfo } from './useAccountInfo';
import { HearthSetupView } from '../DeviceSetup/HearthSetupView';
import { HearthWifiManagement } from './HearthWifiManagement';
import { useFamiliesData } from './useFamiliesData';

/* AppShell — top-level chrome for the post-auth tabs.

   Mounted at /app/* under the main router. Reads accountsInfo via
   useAccountInfo() once and passes the user's families/owners/primary down
   to each tab.

   Routes:
     /app                        → redirect to /app/hearths
     /app/hearths                → HearthsTab list (4.1)
     /app/hearths/:familyID      → HearthDetail (4.2 / 4.3 / 4.4)  [TBD]
     /app/hearths/:fid/settings  → DeviceSettingsView (4.5)         [TBD]
     /app/higlos[*]              → Higlos tab (5.x)                 [TBD]
     /app/help                   → Help (6.x)                       [TBD]
     /app/account                → Account (6.6 / 6.7)              [TBD]

   Detail routes (anything matching /app/<tab>/:id or deeper) hide the tab
   bar via AppShellChrome.showChrome=false; the detail view renders its own
   back-button header.
*/

const TABS = [
  { id: 'hearths', label: 'Hearths', titleLabel: 'Your Hearths', path: '/app/hearths' },
  { id: 'higlos', label: 'Higlos', titleLabel: 'Your Higlos', path: '/app/higlos' },
  { id: 'help', label: 'Help', titleLabel: 'Help', path: '/app/help' },
  { id: 'account', label: 'Account', titleLabel: 'Account', path: '/app/account' },
];

/* AppShellChrome is the pure-visual wrapper — no router-aware logic.
   The preview screens use this directly (with explicit tab content as
   children), so they can render a tab without an inner <Routes>. */
export function AppShellChrome({ activeTab, onTabChange, showChrome = true, tabBadges, children }) {
  const tab = TABS.find((t) => t.id === activeTab);
  if (!showChrome) {
    return <ScreenFrame>{children}</ScreenFrame>;
  }
  const tabsWithBadges = tabBadges
    ? TABS.map((t) => ({ ...t, badge: tabBadges[t.id] }))
    : TABS;
  return (
    <ScreenFrame>
      <div className="h-6 flex-shrink-0" />
      <div className="px-4 pt-2 pb-2 flex items-center justify-between">
        <PageTitle>{tab?.titleLabel || 'Higlo'}</PageTitle>
        <HigloLogo className="w-5 h-5" />
      </div>
      <HTabs tabs={tabsWithBadges} activeId={activeTab} onChange={onTabChange} />
      <div className="flex-1 min-h-0">{children}</div>
    </ScreenFrame>
  );
}

function isDetailRoute(pathname) {
  // Anything deeper than /app/<tab> is treated as a detail page (no tab bar).
  return (
    !!matchPath('/app/hearths/:familyID/*', pathname) ||
    !!matchPath('/app/hearths/:familyID', pathname) ||
    !!matchPath('/app/higlos/:iglooID/*', pathname) ||
    !!matchPath('/app/higlos/:iglooID', pathname) ||
    !!matchPath('/app/higlos/new', pathname) ||
    !!matchPath('/app/help/article/:slug', pathname) ||
    !!matchPath('/app/help/messages', pathname)
  );
}

function activeTabFromPath(pathname) {
  for (const t of TABS) {
    if (pathname.startsWith(t.path)) return t.id;
  }
  return null;
}

/* Stub views for the upcoming phases — let me wire routing today without
   bottoming out at a 404 while I build out the rest. */
function StubTab({ title }) {
  return (
    <div className="px-5 py-12 text-center">
      <p className="text-[13px] text-slate-500">{title} tab coming soon.</p>
    </div>
  );
}

/* HearthSetupRoute — FEAT-133 v9 flow. Pulls familyID from the URL and
   familyName from the live familyInfo snapshot so the breadcrumb can show
   the household name. Back/Done both return to the Hearth detail screen. */
function HearthSetupRoute() {
  const { familyID } = useParams();
  const navigate = useNavigate();
  const families = useFamiliesData(familyID ? [familyID] : []);
  const familyName = families[familyID]?.familyName || 'your Hearth';
  const goBack = () => navigate(`/app/hearths/${familyID}`);
  return (
    <HearthSetupView
      familyID={familyID}
      familyName={familyName}
      onDone={goBack}
      onCancel={goBack}
    />
  );
}

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const account = useAccountInfo();
  /* Live unread-messages count, read from a one-doc listener on
     supportConversations/{accountUUID}. Drives the red dot on the Help
     tab and the same dot on the "Message Us" button inside the tab. */
  const supportUnread = useSupportUnread();

  if (account.loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto mb-3" />
          <p className="text-[13px] text-slate-500">Loading…</p>
        </div>
      </div>
    );
  }

  const activeTab = activeTabFromPath(location.pathname);
  const showChrome = !isDetailRoute(location.pathname);

  return (
    <AppShellChrome
      activeTab={activeTab}
      showChrome={showChrome}
      tabBadges={{ help: supportUnread }}
      onTabChange={(id) => {
        const tab = TABS.find((t) => t.id === id);
        if (tab) navigate(tab.path);
      }}
    >
      <Routes>
        <Route index element={<Navigate to="hearths" replace />} />
        <Route
          path="hearths"
          element={
            <HearthsTab
              familyIDs={account.families}
              owningFamilies={account.owningFamilies}
              primaryFamilyID={account.primaryFamilyID}
              accountUUID={account.accountUUID}
              userEmail={account.userEmail}
            />
          }
        />
        <Route
          path="hearths/:familyID"
          element={
            <HearthDetail
              accountUUID={account.accountUUID}
              primaryFamilyID={account.primaryFamilyID}
              owningFamilies={account.owningFamilies}
              families={account.families}
              userEmail={account.userEmail}
            />
          }
        />
        <Route path="hearths/:familyID/setup" element={<HearthSetupRoute />} />
        <Route path="hearths/:familyID/settings" element={<DeviceSettingsView />} />
        {/* WiFi management (PLAN wifi-change-webapp §4.2) — connection state, current
            network, and the change-credentials flow over the intercom bus. */}
        <Route path="hearths/:familyID/wifi" element={<HearthWifiManagement />} />
        {/* For the Higlo-related routes we pass `account.families` (every
            family the caller manages — owned AND co-managed) as the
            `owningFamilies` prop. useHiglos uses this to decide which
            `familyInfo/{fid}/igloos` subcollections to subscribe to; if
            we passed the narrower `account.owningFamilies` (only families
            with an owners doc) then a pure invitee — who is a controller
            but not an owner — would see their Hearths on the Hearths tab
            but nothing on the Higlos tab, because the hook would
            subscribe to zero families. From the caller's perspective any
            family they control counts as "theirs" for Higlo membership
            purposes. */}
        <Route
          path="higlos"
          element={
            <HiglosTab
              accountUUID={account.accountUUID}
              owningFamilies={account.families}
              primaryFamilyID={account.primaryFamilyID}
            />
          }
        />
        <Route
          path="higlos/:iglooID"
          element={
            <HigloDetail
              accountUUID={account.accountUUID}
              owningFamilies={account.families}
              primaryFamilyID={account.primaryFamilyID}
            />
          }
        />
        <Route
          path="higlos/:iglooID/invite"
          element={
            <InviteFamilyView
              accountUUID={account.accountUUID}
              owningFamilies={account.families}
              primaryFamilyID={account.primaryFamilyID}
            />
          }
        />
        <Route
          path="higlos/:iglooID/position/:familyID"
          element={
            <PositionPicker
              accountUUID={account.accountUUID}
              owningFamilies={account.families}
              primaryFamilyID={account.primaryFamilyID}
            />
          }
        />
        <Route path="help" element={<HelpTab unreadCount={supportUnread} />} />
        <Route path="help/article/:slug/*" element={<ArticleView />} />
        <Route
          path="help/messages"
          element={<MessageUsView accountUUID={account.accountUUID} userEmail={account.userEmail} />}
        />
        <Route
          path="account"
          element={<AccountTab accountUUID={account.accountUUID} userEmail={account.userEmail} />}
        />
        <Route path="*" element={<Navigate to="hearths" replace />} />
      </Routes>
    </AppShellChrome>
  );
}
